<script setup lang="ts">
import { BRILLIANCE_MEMBER_PRICE_EXCLUSIONS } from '~/config/salesforce-ids'
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'

type PromotionListProps = {
  /** Only the ids are read; the full promotions are fetched from SFCC. */
  promotions?: Pick<ShopperProductsTypes.ProductPromotion, 'promotionId'>[]
}

const props = defineProps<PromotionListProps>()
const { getPromotionsByIds, result: promotionsResult } = usePromotions()

const hasLoaded = ref(false)
// Sized from the product response so the SSR render reserves the space the fetched promotions will fill.
const skeletonCount = computed(
  () =>
    props.promotions?.filter((promotion) => promotion.promotionId !== BRILLIANCE_MEMBER_PRICE_EXCLUSIONS).length ?? 0,
)

const getPromotions = async () => {
  const promotionIds = props.promotions?.map((promotion) => promotion.promotionId) || []
  await getPromotionsByIds(promotionIds)
}

const filteredPromotions = computed(() => {
  return (
    promotionsResult.value?.filter(
      (value) => value.id !== BRILLIANCE_MEMBER_PRICE_EXCLUSIONS && (value.c_promotionTitle || value.calloutMsg),
    ) ?? []
  )
})

onMounted(async () => {
  if (!promotionsResult.value?.length) {
    await getPromotions()
  }
  hasLoaded.value = true
})

watch(
  () => props.promotions,
  (newPromos, oldPromos) => {
    if (
      oldPromos &&
      JSON.stringify(newPromos?.sort((a, b) => a.promotionId.localeCompare(b.promotionId))) !==
        JSON.stringify(oldPromos?.sort((a, b) => a.promotionId.localeCompare(b.promotionId)))
    ) {
      getPromotions()
    }
  },
)
</script>

<template>
  <div class="pdp-promotions">
    <ul v-if="!hasLoaded && skeletonCount" class="pdp-promotions__list" aria-hidden="true">
      <li v-for="n in skeletonCount" :key="n" class="pdp-promotions__list-item pdp-promotions__skeleton">
        <div class="skeleton-text" />
      </li>
    </ul>
    <ul v-else-if="filteredPromotions && filteredPromotions.length > 0" class="pdp-promotions__list">
      <li v-for="promotion in filteredPromotions" :key="promotion.id" class="pdp-promotions__list-item">
        <StandardPromotion :promotion="promotion" />
      </li>
    </ul>
  </div>
</template>

<style lang="postcss">
.pdp-promotions {
  .pdp-promotions__list {
    margin-block-end: 0;
    margin-block-start: 0;
  }
  .pdp-promotions__list-item {
    margin-bottom: var(--space-30);
  }
  .pdp-promotions__skeleton {
    display: flex;
    align-items: center;
    /* One line of .pdp-promotion title text */
    font-size: var(--text-small);
    height: 1lh;
    .skeleton-text {
      width: 60%;
      height: 0.875em;
    }
  }
}
</style>
